import { FC, useEffect, useRef, useState } from "react";
import { Box, Grid, Table, TableBody, TableCell, TableContainer, TableHead, TableRow } from "@mui/material";
import { backend, displayDateAndTime, DetailedStatsHistoryItem } from "Backend";
import StatsCard from "routes/lens-editor/StatsCard";
import { P } from "theme/typography";
import useInterval from "helpers/hooks/useInterval";
import { groupBy } from "helpers/coll";

const Stats: FC = () => {
  const [statsHistory, setStatsHistory] = useState<DetailedStatsHistoryItem[]>([]);
  const isInBackgroundRef = useRef(false);

  const fetchStats = async () => {
    if (!isInBackgroundRef.current) {
      const result = await backend.getDetailedInfraStats();
      const currentTime = Date.now();
      // Add the new stats to the history
      const newDetailedStatsHistoryItem: DetailedStatsHistoryItem = {
        timestamp: currentTime,
        data: result,
      };

      setStatsHistory((prevStatsHistory) => {
        if (prevStatsHistory.length >= 180) {
          // Remove the oldest item
          prevStatsHistory.shift();
        }

        // Add the new item
        return [...prevStatsHistory, newDetailedStatsHistoryItem];
      });
    }
  };

  useEffect(() => {
    void fetchStats();
    const handleVisibilityChange = () => {
      isInBackgroundRef.current = document.hidden;
    };
    document.addEventListener("visibilitychange", handleVisibilityChange);
    return () => {
      // Use the same function reference to remove the event listener
      document.removeEventListener("visibilitychange", handleVisibilityChange);
    };
  }, []);

  useInterval(fetchStats, 10000);

  const lastTimestamp = statsHistory[statsHistory.length - 1]?.timestamp;

  return (
    <Box sx={{ display: "flex", flexDirection: "column", flex: 1, height: "100%", px: 1 }}>
      <Box
        sx={{
          display: "flex",
          flexDirection: "row",
          justifyContent: "space-between",
          alignItems: "center",
          mb: 2,
        }}>
        <Box>
          <P variant="xl2Bold">Infrastructure Stats</P>
        </Box>
        <Box
          sx={{
            display: "flex",
            justifyContent: "flex-end",
            gap: 2,
          }}>
          <Box sx={{ display: "flex", alignItems: "center", gap: 1 }}>
            <P>
              Last refresh:
              {lastTimestamp !== undefined ? displayDateAndTime(new Date(lastTimestamp)) : "Never"}
            </P>
          </Box>
        </Box>
      </Box>
      <Grid
        container
        spacing={2}
        sx={{
          overflowY: "auto",
          pb: 2,
        }}>
        <Grid item xl={6} sm={12}>
          <StatsCard
            title="Scheduled Event Queue"
            subtitle="System scheduled events, such as autorepair and SFTP transfer, Autoreview chats, and Case Exports"
            data={[
              {
                title: "Queue",
                dataPoints: statsHistory.map((h) => {
                  return {
                    timestamp: h.timestamp,
                    value: h.data.sqsStats.scheduledEvent,
                  };
                }),
              },
              {
                title: "Priority Queue",
                dataPoints: statsHistory.map((h) => {
                  return {
                    timestamp: h.timestamp,
                    value: h.data.sqsStats.scheduledEventPriorityCount,
                  };
                }),
              },
            ]}
            displayInflightStats={true}
          />
        </Grid>
        <Grid item xl={6} sm={12}>
          <StatsCard
            title="Process Queue"
            subtitle="All document processing, excluding lens runs, except runs that result from processing"
            data={[
              {
                title: "Background Queue ",
                dataPoints: statsHistory.map((h) => {
                  return {
                    timestamp: h.timestamp,
                    value: h.data.sqsStats.processBackgroundCount,
                  };
                }),
              },
              {
                title: "Queue",
                dataPoints: statsHistory.map((h) => {
                  return {
                    timestamp: h.timestamp,
                    value: h.data.sqsStats.processCount,
                  };
                }),
              },
              {
                title: "Priority Queue",
                dataPoints: statsHistory.map((h) => {
                  return {
                    timestamp: h.timestamp,
                    value: h.data.sqsStats.processPriorityCount,
                  };
                }),
              },
            ]}
            displayInflightStats={true}
          />
        </Grid>
        <Grid item xl={6} sm={12}>
          <StatsCard
            title="ClientId Queue Stats"
            subtitle="Queue counts for each clientId"
            data={Array.from(
              groupBy(
                statsHistory.flatMap((historyItem) => {
                  return historyItem.data.clientIdStats.map((stat) => {
                    return {
                      key: stat.clientId,
                      timestamp: historyItem.timestamp,
                      value: stat.stats,
                    };
                  });
                }),
                (t) => t.key
              )
            ).map(([a, b]) => {
              return {
                title: a.toString(),
                dataPoints: b.map((v) => {
                  return {
                    timestamp: v.timestamp,
                    value: v.value,
                  };
                }),
              };
            })}
            displayInflightStats={false}
          />
        </Grid>
        <Grid item xl={6} sm={12}>
          <StatsCard
            title="JobType Queue Stats"
            subtitle="Queue counts for each jobType"
            data={Array.from(
              groupBy(
                statsHistory.flatMap((historyItem) => {
                  return historyItem.data.jobTypeStats.map((stat) => {
                    return {
                      key: stat.jobType,
                      timestamp: historyItem.timestamp,
                      value: stat.stats,
                    };
                  });
                }),
                (t) => t.key
              )
            ).map(([a, b]) => {
              return {
                title: a,
                dataPoints: b.map((v) => {
                  return {
                    timestamp: v.timestamp,
                    value: v.value,
                  };
                }),
              };
            })}
            displayInflightStats={false}
          />
        </Grid>

        <Grid item xl={6} sm={12}>
          <StatsCard
            title="Detailed Queue Stats"
            subtitle="Queue counts broken down by clientId, jobType, and queueId"
            data={Array.from(
              groupBy(
                statsHistory.flatMap((historyItem) => {
                  return historyItem.data.unpartitionedStats.map((stat) => {
                    return {
                      key: stat.name,
                      timestamp: historyItem.timestamp,
                      value: stat.stats,
                    };
                  });
                }),
                (t) => t.key
              )
            ).map(([a, b]) => {
              return {
                title: a,
                dataPoints: b.map((v) => {
                  return {
                    timestamp: v.timestamp,
                    value: v.value,
                  };
                }),
              };
            })}
            displayInflightStats={false}
          />
        </Grid>
        <Grid item xl={6} sm={12}>
          <p>Detailed Queue Stats Table</p>
          <TableContainer
            sx={{
              maxHeight: "400px",
            }}>
            <Table stickyHeader>
              <TableHead>
                <TableRow>
                  <TableCell>Key</TableCell>
                  <TableCell>Count</TableCell>
                </TableRow>
              </TableHead>
              <TableBody>
                {statsHistory[statsHistory.length - 1]?.data.unpartitionedStats
                  .toSorted((stat1, stat2) => stat2.stats.queueSize - stat1.stats.queueSize)
                  .map((stat) => {
                    return (
                      <TableRow key={stat.name}>
                        <TableCell>{stat.name}</TableCell>
                        <TableCell>{stat.stats.queueSize}</TableCell>
                      </TableRow>
                    );
                  })}
              </TableBody>
            </Table>
          </TableContainer>
        </Grid>
      </Grid>
    </Box>
  );
};

export default Stats;
